import React, { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  formatPlannerEditableDisplayValue,
  getPlannerDisplayDecimals,
  getPlannerEditableRawValue,
  hasPlannerValueChanged,
  normalizePlannerRawInput,
  parsePlannerEditedValue,
} from '../../lib/PlannerEditableCell'

/**
 * Monthly planning grid for the new (workspace.*) planning UI.
 *
 * Mirrors the structure/labels from the fresh-start wireframe and the WeeklyPlanningGrid.
 *
 * Props:
 *  - columns: [{ key, label, isCurrent, isFuture, index }]
 *  - data: array of per-month objects (indexed by column.index)
 *  - summary: object with aggregate/summary values
 *  - onChange: (monthIndex, field, value) => void
 */
function MonthlyPlanningGrid({ columns, data, summary, onChange, onCopyActuals }) {
  const safeColumns = useMemo(() => columns || [], [columns])
  const [inventoryOnOrderExpanded, setInventoryOnOrderExpanded] = useState(false)
  const [editValues, setEditValues] = useState({})
  const displaySummary = useMemo(() => summary || null, [summary])

  const formatFreshnessPercent = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n)) return null
    return `${Math.round(n * 100)}%`
  }

  const inventoryFreshnessDisplay = useMemo(() => {
    const currentColumn = safeColumns.find((column) => column?.isCurrent) || safeColumns[0] || null
    const selectedMonth = currentColumn ? (data?.[currentColumn.index] || null) : null
    return formatFreshnessPercent(selectedMonth?.freshness)
  }, [safeColumns, data])

  const formatNumber = (v, decimals = 0) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return '-'
    const d = Math.min(1, Math.max(0, Number(decimals) || 0))
    if (d > 0) return n.toFixed(d)
    return Math.round(n).toLocaleString()
  }

  const clearEditValue = (cellKey) => {
    setEditValues((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, cellKey)) return prev
      const next = { ...prev }
      delete next[cellKey]
      return next
    })
  }

  const renderCell = ({
    value,
    editable = false,
    decimals = 0,
    isCurrent = false,
    isFuture = false,
    field,
    monthIndex,
    showOrangeWhenDifferent = false,
    showRedWhenBelowComparison = false,
    comparisonValue = null,
    showRedWhenNegative = false,
  }) => {
    const displayDecimals = getPlannerDisplayDecimals(field, decimals)
    const showRed = (showRedWhenNegative && Number(value ?? 0) < 0) ||
      (showRedWhenBelowComparison && Number(comparisonValue ?? 0) > 0 && Number(value ?? 0) < Number(comparisonValue ?? 0))

    if (editable) {
      const cellKey = `${field}-${monthIndex}`
      const normalized = (value === null || typeof value === 'undefined') ? '' : value
      const display = Object.prototype.hasOwnProperty.call(editValues, cellKey)
        ? editValues[cellKey]
        : formatPlannerEditableDisplayValue(field, normalized, displayDecimals)
      return (
        <TextField
          fullWidth
          value={display}
          onFocus={() => {
            const rawOnFocus = getPlannerEditableRawValue(field, normalized, displayDecimals)
            console.log(`[MonthlyPlanningGrid] onFocus  field=${field} monthIndex=${monthIndex} cellKey=${cellKey} normalized=${normalized} rawOnFocus=${rawOnFocus} alreadyTracked=${Object.prototype.hasOwnProperty.call(editValues, cellKey)}`)
            if (Object.prototype.hasOwnProperty.call(editValues, cellKey)) return
            setEditValues((prev) => ({ ...prev, [cellKey]: rawOnFocus }))
          }}
          onChange={(e) => {
            const raw = normalizePlannerRawInput(e.target.value)
            console.log(`[MonthlyPlanningGrid] onChange field=${field} monthIndex=${monthIndex} cellKey=${cellKey} raw=${raw} displayDecimals=${displayDecimals}`)
            if (displayDecimals > 0) {
              if (!/^\d*(\.\d*)?$/.test(raw)) { console.log(`[MonthlyPlanningGrid] onChange REJECTED (decimal pattern) field=${field}`); return }
              setEditValues((prev) => ({ ...prev, [cellKey]: raw }))
              if (raw === '' || raw === '.') return
              const parsed = parsePlannerEditedValue(field, raw, displayDecimals)
              if (parsed === null) { console.log(`[MonthlyPlanningGrid] onChange parsed=null field=${field}`); return }
              console.log(`[MonthlyPlanningGrid] onChange calling onChange prop field=${field} parsed=${parsed}`)
              onChange?.(monthIndex, field, parsed)
              return
            }

            if (!/^\d*$/.test(raw)) { console.log(`[MonthlyPlanningGrid] onChange REJECTED (integer pattern) field=${field} raw=${raw}`); return }
            setEditValues((prev) => ({ ...prev, [cellKey]: raw }))
            const parsed = parsePlannerEditedValue(field, raw, displayDecimals)
            if (parsed === null) { console.log(`[MonthlyPlanningGrid] onChange parsed=null field=${field}`); return }
            console.log(`[MonthlyPlanningGrid] onChange calling onChange prop field=${field} parsed=${parsed}`)
            onChange?.(monthIndex, field, parsed)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Tab') {
              console.log(`[MonthlyPlanningGrid] onKeyDown TAB field=${field} monthIndex=${monthIndex} cellKey=${cellKey} currentInputValue=${e.target.value} editValueTracked=${Object.prototype.hasOwnProperty.call(editValues, cellKey)} editValueStored=${editValues[cellKey]}`)
            }
          }}
          onBlur={(e) => {
            const rawBlur = normalizePlannerRawInput(e?.target?.value ?? editValues[cellKey])
            const isTracked = Object.prototype.hasOwnProperty.call(editValues, cellKey)
            const changed = isTracked ? hasPlannerValueChanged(field, rawBlur, value, displayDecimals) : false
            console.log(`[MonthlyPlanningGrid] onBlur  field=${field} monthIndex=${monthIndex} cellKey=${cellKey} rawBlur=${rawBlur} currentPropValue=${value} isTracked=${isTracked} hasChanged=${changed} relatedTarget=${e?.relatedTarget?.tagName ?? 'null'}`)
            if (!isTracked) return
            if (changed) {
              const parsed = parsePlannerEditedValue(field, rawBlur, displayDecimals)
              console.log(`[MonthlyPlanningGrid] onBlur  COMMITTING field=${field} parsed=${parsed}`)
              if (parsed !== null) onChange?.(monthIndex, field, parsed)
            } else {
              console.log(`[MonthlyPlanningGrid] onBlur  NO COMMIT (value unchanged) field=${field} rawBlur=${rawBlur} propValue=${value}`)
            }
            clearEditValue(cellKey)
          }}
          variant="outlined"
          size="small"
          type="text"
          inputProps={{
            'data-testid': `monthly-input-${field}-${monthIndex}`,
            'aria-label': `monthly-${field}-${monthIndex}`,
            inputMode: displayDecimals > 0 ? 'decimal' : 'numeric',
            style: {
              textAlign: 'center',
              paddingTop: 6,
              paddingBottom: 6,
              paddingLeft: 0,
              paddingRight: 0,
              fontSize: 13,
            },
          }}
          sx={{
            width: '100%',
              '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
              '& input': {
                color: showRed ? '#b91c1c' : 'rgba(0, 0, 0, 0.87)',
                fontWeight: showRed ? 700 : 600,
              },
            },
            // Remove inner padding so the input can use the full cell width.
            '& .MuiOutlinedInput-input': {
              paddingLeft: '0px !important',
              paddingRight: '0px !important',
            },
            '& input[type=number]': { MozAppearance: 'textfield' },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }}
        />
      )
    }

    const showOrange = !showRed && ((isFuture && Number(value) === 0) || (showOrangeWhenDifferent && isFuture))

    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: 13,
          textAlign: 'center',
          fontWeight: showRed ? 700 : (isCurrent ? 700 : 500),
          color: showRed ? '#b91c1c' : (showOrange ? '#f97316' : 'inherit'),
        }}
      >
        {formatNumber(value, displayDecimals)}
      </Typography>
    )
  }

  const sectionHeader = (label, color, options = {}) => {
    const bg = color === 'green' ? '#d1fae5' : color === 'yellow' ? '#fef3c7' : '#fee2e2'
    const fg = color === 'green' ? '#065f46' : color === 'yellow' ? '#92400e' : '#991b1b'
    const {
      showToggle = false,
      expanded = true,
      onToggle = null,
      toggleLabel = '',
      metaLabel = null,
      metaValue = null,
      metaTestId = null,
    } = options
    return (
      <TableRow>
        <TableCell colSpan={safeColumns.length + 2} sx={{ backgroundColor: bg, color: fg, fontWeight: 700, fontSize: 12, py: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.2 }}>
              <span>{label}</span>
              {metaLabel && metaValue ? (
                <Typography variant="caption" data-testid={metaTestId || undefined} sx={{ fontSize: 10, fontWeight: 700, color: fg, lineHeight: 1.1 }}>
                  {`${metaLabel} ${metaValue}`}
                </Typography>
              ) : null}
            </Box>
            {showToggle ? (
              <Tooltip title={expanded ? `Collapse ${toggleLabel}` : `Expand ${toggleLabel}`}>
                <IconButton
                  size="small"
                  aria-label={expanded ? `Collapse ${toggleLabel}` : `Expand ${toggleLabel}`}
                  onClick={onToggle}
                  sx={{
                    color: fg,
                    border: '1px solid rgba(146,64,14,0.25)',
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.8)',
                    },
                  }}
                >
                  {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>
      </TableRow>
    )
  }

  const rowBg = (sectionColor) => {
    if (sectionColor === 'green') return '#ecfdf5'
    if (sectionColor === 'yellow') return '#fffbeb'
    if (sectionColor === 'red') return '#fef2f2'
    return '#fff'
  }

  const renderRow = (label, field, { editable = false, decimals = 0, summaryKey = field, showOrangeWhenDifferent = false, showRedWhenBelowComparison = false, comparisonField = null, sectionColor, copyKey = null, showRedWhenNegative = false } = {}) => {
    return (
      <TableRow>
        <TableCell
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: rowBg(sectionColor),
            fontWeight: 600,
            fontSize: 12,
            minWidth: 160,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
            {copyKey && typeof onCopyActuals === 'function' ? (
              <Tooltip title="Copy Actuals to Plan">
                <Chip
                  size="small"
                  color="success"
                  icon={<ContentCopyIcon />}
                  onClick={() => onCopyActuals(copyKey)}
                  sx={{ height: 22 }}
                />
              </Tooltip>
            ) : null}
          </Box>
        </TableCell>

        {safeColumns.map((c) => {
          const m = data?.[c.index] || {}
          const value = m?.[field]
          return (
            <TableCell
              key={`${field}-${c.key}`}
              sx={{
                backgroundColor: c.isCurrent ? '#4c1d95' : rowBg(sectionColor),
                color: c.isCurrent ? '#fff' : 'inherit',
                py: 0.5,
              }}
            >
              {renderCell({
                value,
                editable,
                decimals,
                isCurrent: c.isCurrent,
                isFuture: c.isFuture,
                field,
                monthIndex: c.index,
                showOrangeWhenDifferent,
                showRedWhenBelowComparison,
                comparisonValue: comparisonField ? m?.[comparisonField] : null,
                showRedWhenNegative,
              })}
            </TableCell>
          )
        })}

        <TableCell sx={{ backgroundColor: rowBg(sectionColor), fontWeight: 700, fontSize: 13, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 13,
              fontWeight: showRedWhenBelowComparison && comparisonField && Number(displaySummary?.[comparisonField] ?? 0) > 0 && Number(displaySummary?.[summaryKey] ?? 0) < Number(displaySummary?.[comparisonField] ?? 0) ? 700 : 600,
              color: showRedWhenBelowComparison && comparisonField && Number(displaySummary?.[comparisonField] ?? 0) > 0 && Number(displaySummary?.[summaryKey] ?? 0) < Number(displaySummary?.[comparisonField] ?? 0) ? '#b91c1c' : 'inherit',
            }}
          >
            {displaySummary && typeof displaySummary?.[summaryKey] !== 'undefined' ? formatNumber(displaySummary[summaryKey], getPlannerDisplayDecimals(field, decimals)) : '-'}
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 1 }}>
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: '#f3f4f6',
                  minWidth: 160,
                }}
              />
              {safeColumns.map((c) => (
                <TableCell
                  key={c.key}
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    textAlign: 'center',
                    minWidth: 80,
                    backgroundColor: c.isCurrent ? '#4c1d95' : '#f3f4f6',
                    color: c.isCurrent ? '#fff' : 'inherit',
                  }}
                >
                  {(() => {
                    const match = typeof c.label === 'string' && c.label.match(/^(.+?)\s+(\d+\s*\/\s*\d+)$/)
                    if (match) {
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2 }}>
                          <span>{match[1]}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>{match[2]}</span>
                        </Box>
                      )
                    }
                    return c.label
                  })()}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700, fontSize: 13, textAlign: 'center', minWidth: 90, backgroundColor: '#f3f4f6' }}>
                Summary
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sectionHeader('Sales', 'green')}
            {renderRow('Actual', 'salesActual', { sectionColor: 'green' })}
            {renderRow('Plan', 'salesPlan', { sectionColor: 'green', showOrangeWhenDifferent: true, showRedWhenBelowComparison: true, comparisonField: 'salesActual', editable: true })}
            {renderRow('Plan %', 'planPercent', { sectionColor: 'green', editable: true, decimals: 1 })}
            {renderRow('Actual %', 'actualPercent', { sectionColor: 'green', decimals: 1, copyKey: 'sales' })}

            {sectionHeader('Inventory', 'yellow', {
              showToggle: true,
              expanded: inventoryOnOrderExpanded,
              onToggle: () => setInventoryOnOrderExpanded((prev) => !prev),
              toggleLabel: 'On Order rows',
              metaLabel: 'Freshness',
              metaValue: inventoryFreshnessDisplay,
              metaTestId: 'monthly-inventory-header-freshness',
            })}
            {renderRow('Actual Inv BOM', 'actualInvBOM', { sectionColor: 'yellow' })}
            {renderRow('Actual S/S', 'actualSS', { sectionColor: 'yellow', decimals: 1, copyKey: 'inventory' })}
            {renderRow('Plan S/S', 'planSS', { sectionColor: 'yellow', editable: true, decimals: 1 })}
            {renderRow('Plan Inv BOM', 'planInvBOM', { sectionColor: 'yellow', editable: true })}
            {renderRow('Plan Inflow Retail', 'planInflowRetail', { sectionColor: 'yellow', showRedWhenNegative: true })}
            {renderRow('Plan Inflow Cost', 'planInflowCost', { sectionColor: 'yellow', showRedWhenNegative: true })}
            {renderRow('Actual Inflow Retail', 'inflowRetailActual', { sectionColor: 'yellow' })}
            {renderRow('Actual Inflow Cost', 'inflowCostActual', { sectionColor: 'yellow' })}
            {inventoryOnOrderExpanded ? renderRow('On Order Retail MTD', 'onOrderRetailMtd', { sectionColor: 'yellow' }) : null}
            {inventoryOnOrderExpanded ? renderRow('On Order Cost MTD', 'onOrderCostMtd', { sectionColor: 'yellow' }) : null}

            {sectionHeader('Markdowns', 'red')}
            {renderRow('Actual', 'markdownActual', { sectionColor: 'red' })}
            {renderRow('Plan', 'markdownPlan', { sectionColor: 'red', showOrangeWhenDifferent: true, editable: true })}
            {renderRow('Plan % MD', 'markdownPlanPercent', { sectionColor: 'red', editable: true, decimals: 1 })}
            {renderRow('Actual % MD', 'markdownActualPercent', { sectionColor: 'red', decimals: 1, copyKey: 'markdown' })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default MonthlyPlanningGrid

